"use client";

import Link from "next/link";
import { ArrowRight, ShieldCheck, Zap, Lock } from "lucide-react";
import { siteConfig } from "@/config/site";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Icons } from "@/components/shared/icons";
import BlurText from "@/components/ui/blur-text";
import { motion, type Transition, type Variants } from "motion/react";

interface HeroLandingProps {
  staggerInterval?: number;
  itemOffsetY?: number;
  itemTransition?: Transition;
}

const defaultItemTransition: Transition = {
  type: "spring",
  stiffness: 120,
  damping: 20,
};

export default function HeroLanding({
  staggerInterval = 0.12,
  itemOffsetY = 36,
  itemTransition = defaultItemTransition,
}: HeroLandingProps = {}) {
  const containerVariants: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: staggerInterval,
        delayChildren: 0.05,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: itemOffsetY, filter: "blur(6px)" },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: itemTransition,
    },
  };

  return (
    <section className="relative overflow-hidden pt-12 pb-16 md:pt-20 md:pb-24">
      {/**
       * Prime-number durations (23/19s and 17/29s) ensure
       * x and y never sync up, creating an organic,
       * non-repeating drift pattern from simple keyframes.
       */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{
          opacity: [0.18, 0.28, 0.18],
          x: [0, 60, -40, 20, 0],
          y: [0, -30, 50, -20, 0],
        }}
        transition={{
          opacity: { duration: 1.2, ease: "easeOut" },
          x: { duration: 23, repeat: Infinity, ease: "easeInOut" },
          y: { duration: 19, repeat: Infinity, ease: "easeInOut" },
        }}
        className="pointer-events-none absolute -top-24 left-1/2 -z-10 size-[620px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(99,102,241,0.25)_0%,transparent_70%)] blur-3xl dark:bg-[radial-gradient(ellipse_at_center,rgba(129,140,248,0.28)_0%,transparent_70%)]"
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{
          opacity: [0.14, 0.24, 0.14],
          x: [0, -50, 30, -60, 0],
          y: [0, 40, -30, 50, 0],
        }}
        transition={{
          opacity: { duration: 1.4, ease: "easeOut", delay: 0.2 },
          x: { duration: 17, repeat: Infinity, ease: "easeInOut" },
          y: { duration: 29, repeat: Infinity, ease: "easeInOut" },
        }}
        className="pointer-events-none absolute top-36 right-1/4 -z-10 size-[480px] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(168,85,247,0.2)_0%,transparent_70%)] blur-3xl dark:bg-[radial-gradient(ellipse_at_center,rgba(192,132,252,0.22)_0%,transparent_70%)]"
      />

      <motion.div
        className="container flex max-w-5xl flex-col items-center gap-6 text-center"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Status Pill Badge */}
        <motion.div
          variants={itemVariants}
          whileHover={{ scale: 1.03 }}
          transition={{ type: "spring", stiffness: 400, damping: 25 }}
          className="inline-flex items-center gap-2 rounded-full border border-indigo-500/20 bg-indigo-500/10 px-4 py-1.5 text-xs font-semibold text-indigo-700 shadow-sm backdrop-blur-sm dark:border-indigo-400/25 dark:bg-indigo-950/40 dark:text-indigo-300"
        >
          <span className="relative flex size-2">
            <motion.span
              className="absolute inline-flex size-full rounded-full bg-emerald-400"
              animate={{ scale: [1, 2.2, 1], opacity: [0.75, 0, 0.75] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            />
            <span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
          </span>
          <span>All Systems Operational</span>
          <span className="text-muted-foreground">•</span>
          <span className="text-emerald-700 dark:text-emerald-400 font-bold">100% Undetected</span>
        </motion.div>

        {/* Main Headline with BlurText */}
        <motion.div variants={itemVariants} className="w-full flex justify-center">
          <BlurText
            text="Next-Gen Gaming Software & Recoil Macros"
            delay={100}
            animateBy="words"
            direction="bottom"
            stepDuration={0.35}
            as="h1"
            className="text-balance font-urban text-4xl font-extrabold tracking-tight sm:text-6xl md:text-7xl lg:text-[72px]"
            highlightWords={["Recoil", "Macros"]}
            highlightClassName="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent"
          />
        </motion.div>

        {/* Subtitle */}
        <motion.p
          variants={itemVariants}
          className="max-w-2xl text-balance text-base leading-relaxed text-muted-foreground sm:text-xl sm:leading-8"
        >
          Gain the competitive edge with military-grade recoil control, ESP visuals, and hardware-emulated macros for PUBG, CS2, and more. Instant license key delivery right after checkout.
        </motion.p>

        {/* Action CTAs */}
        <motion.div variants={itemVariants} className="mt-2 flex flex-wrap justify-center gap-3 md:gap-4">
          <motion.a
            href="#products"
            whileHover={{
              y: -2,
              scale: 1.02,
              boxShadow: "0 10px 30px -10px rgba(99, 102, 241, 0.5)",
            }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
            className={cn(
              buttonVariants({ size: "lg", rounded: "full" }),
              "gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 px-7 font-bold text-white shadow-lg shadow-indigo-500/25 hover:from-indigo-500 hover:to-purple-500 cursor-pointer",
            )}
          >
            <span>Explore Products</span>
            <motion.span
              animate={{ x: [0, 3, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            >
              <ArrowRight className="size-4" />
            </motion.span>
          </motion.a>

          <motion.div
            whileHover={{ y: -2, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
          >
            <Link
              href={siteConfig.links.discord ?? "https://discord.gg"}
              target="_blank"
              rel="noreferrer"
              className={cn(
                buttonVariants({
                  variant: "outline",
                  size: "lg",
                  rounded: "full",
                }),
                "gap-2 px-6 font-semibold border-border hover:bg-accent",
              )}
            >
              <Icons.discord className="size-4" />
              <span>Join Discord</span>
            </Link>
          </motion.div>
        </motion.div>

        {/* Trust Badges Row */}
        <motion.div
          variants={itemVariants}
          className="mt-6 flex flex-wrap items-center justify-center gap-6 text-xs text-muted-foreground sm:gap-8"
        >
          <motion.div
            whileHover={{ y: -1.5, scale: 1.03 }}
            className="flex items-center gap-1.5 font-medium transition-colors"
          >
            <Zap className="size-4 text-amber-500" />
            <span>Instant Key Dispatch</span>
          </motion.div>
          <motion.div
            whileHover={{ y: -1.5, scale: 1.03 }}
            className="flex items-center gap-1.5 font-medium transition-colors"
          >
            <ShieldCheck className="size-4 text-emerald-500" />
            <span>Kernel-Level Stealth</span>
          </motion.div>
          <motion.div
            whileHover={{ y: -1.5, scale: 1.03 }}
            className="flex items-center gap-1.5 font-medium transition-colors"
          >
            <Lock className="size-4 text-indigo-500" />
            <span>Hardware Emulation</span>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
}
